import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import {
  BookOpen,
  Play,
  Eye,
  Clock,
  Star,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  ThumbsUp,
  MessageCircle,
  Share2,
  Crown,
  Sparkles,
} from 'lucide-react'
import { api } from '@/lib/api'
import { getTeacherName, formatPrice, formatDate, formatName, resolveImg } from '@/lib/helpers'
import Avatar from '@/components/ui/Avatar'
import {
  HeroSkeleton,
  FeaturedCardSkeleton,
  CourseCardSkeleton,
  VideoCardSkeleton,
  TeacherCardSkeleton,
  PostCardSkeleton,
} from '@/components/ui/Skeleton'
import { useAuth } from '@/context/AuthContext'

/* ── FAQ Item ── */
function FAQItem({
  item,
  isOpen,
  onClick,
}: {
  item: { question: string; answer: string }
  isOpen: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className="bg-white rounded-2xl shadow-xs border-b border-gray-200 p-6 flex gap-4 items-start justify-center w-full text-left cursor-pointer"
    >
      <div className="flex-1">
        <p className="text-base font-medium text-black">{item.question}</p>
        {isOpen && (
          <p className="text-sm text-gray-500 mt-3">{item.answer}</p>
        )}
      </div>
      <ChevronDown
        className={`size-6 text-gray-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
      />
    </button>
  )
}

/* ── Featured Card (Teacher) ── */
function FeaturedTeacherCard({ teacher }: { teacher: any }) {
  const name = formatName(teacher.userId?.fullName) || ''
  const cover = resolveImg(teacher.coverImage || teacher.userId?.avatar)

  return (
    <Link
      to={`/teachers/${teacher._id}`}
      className="bg-white border border-gray-200 rounded-3xl overflow-hidden flex flex-col flex-1 min-w-0 group"
    >
      {/* Cover */}
      <div className="h-[246px] relative">
        {cover ? (
          <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-amber-100 to-amber-200" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/25" />
        {/* Badge */}
        <div className="absolute top-4 left-4">
          <span className="backdrop-blur-sm bg-[#004da6] border-3 border-white/20 text-white text-xs font-semibold uppercase px-2 py-1 rounded-full">
            Онцлох Багш
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 flex flex-col gap-3 flex-1">
        <div>
          <p className="font-semibold text-gray-800">{name}</p>
          <p className="text-xs font-bold text-accent-500 uppercase mt-1">
            {teacher.title || teacher.specialty || ''}
          </p>
        </div>
        <p className="text-sm text-gray-400 line-clamp-2">{teacher.description}</p>
        <hr className="border-gray-200" />
        <button className="bg-gray-50 rounded-full py-2.5 text-sm font-semibold text-black hover:bg-gray-100 transition text-center">
          Дэлгэрэнгүй
        </button>
      </div>
    </Link>
  )
}

/* ── Featured Card (Course) ── */
function FeaturedCourseCard({ course }: { course: any }) {
  const cover = resolveImg(course.thumbnail || course.thumbnailUrl || course.coverImage)
  return (
    <Link
      to={`/courses/${course._id}`}
      className="bg-white border border-gray-200 rounded-3xl overflow-hidden flex flex-col flex-1 min-w-0 group"
    >
      <div className="h-[246px] relative">
        {cover ? (
          <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-blue-200" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/25" />
        <div className="absolute top-4 left-4">
          <span className="backdrop-blur-sm bg-accent-500 border-3 border-white/20 text-white text-xs font-semibold uppercase px-2 py-1 rounded-full">
            Онцлох хичээл
          </span>
        </div>
      </div>
      <div className="p-6 flex flex-col gap-3 flex-1">
        <div className="flex-1">
          <p className="font-semibold text-gray-800 truncate">{course.title}</p>
          <div className="flex items-center gap-2.5 mt-1 text-sm">
            <span className="text-gray-600">{getTeacherName(course)}</span>
            <span className="size-[3px] rounded-full bg-gray-400" />
            <span className="flex items-center gap-1 text-gray-400">
              <BookOpen className="size-4" />
              {course.lessonCount || 0} хичээл
            </span>
          </div>
        </div>
        <p className="text-sm text-gray-400 line-clamp-2">{course.description}</p>
        <hr className="border-gray-200" />
        <button className="bg-gray-50 rounded-full py-2.5 text-sm font-semibold text-black hover:bg-gray-100 transition text-center">
          Хичээл үзэх
        </button>
      </div>
    </Link>
  )
}

/* ── Featured Card (Video) ── */
function FeaturedVideoCard({ video }: { video: any }) {
  const cover = resolveImg(video.thumbnail || video.thumbnailUrl || video.coverImage)
  return (
    <Link
      to={`/videos/${video._id}`}
      className="bg-white border border-gray-200 rounded-3xl overflow-hidden flex flex-col flex-1 min-w-0 group"
    >
      <div className="h-[246px] relative">
        {cover ? (
          <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-gray-700 to-gray-900" />
        )}
        <div className="absolute inset-0 backdrop-blur-[0.5px]" />
        <div className="absolute top-4 left-4">
          <span className="backdrop-blur-sm bg-black/50 border-3 border-white/20 text-white text-xs font-semibold uppercase px-1.5 py-1 rounded-full">
            Онцлох видео
          </span>
        </div>
      </div>
      <div className="p-6 flex flex-col gap-3 flex-1">
        <div className="flex-1">
          <p className="font-semibold text-gray-800 truncate">{video.title}</p>
          <div className="flex items-center gap-2.5 mt-1 text-sm">
            <span className="text-gray-600">{getTeacherName(video)}</span>
            <span className="size-[3px] rounded-full bg-gray-400" />
            <span className="flex items-center gap-1 text-gray-400">
              <Play className="size-4" />
              {video.duration || '180 мин'}
            </span>
          </div>
        </div>
        <p className="text-sm text-gray-400 line-clamp-2">{video.description}</p>
        <hr className="border-gray-200" />
        <button className="bg-gray-50 rounded-full py-2.5 text-sm font-semibold text-black hover:bg-gray-100 transition text-center">
          Видео үзэх
        </button>
      </div>
    </Link>
  )
}

/* ── Course Card ── */
function CourseCard({ course }: { course: any }) {
  const cover = resolveImg(course.thumbnail || course.thumbnailUrl || course.coverImage)
  return (
    <Link to={`/courses/${course._id}`} className="group">
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col">
        {/* Image */}
        <div className="h-[170px] relative overflow-hidden shrink-0">
          {cover ? (
            <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-amber-100 to-amber-200" />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/25" />
          {/* Category badge */}
          <div className="absolute top-3 left-3 flex gap-1.5">
            {course.category && (
              <span className="backdrop-blur-sm bg-black/50 text-white text-xs font-semibold uppercase px-1.5 py-1 rounded">
                {course.category}
              </span>
            )}
            {course.isFeatured && (
              <span className="backdrop-blur-sm bg-accent-500 text-white text-xs font-semibold uppercase px-1.5 py-1 rounded">
                Онцлох
              </span>
            )}
          </div>
          {/* Rating + Premium badges */}
          <div className="absolute bottom-3 left-3 flex gap-1.5">
            {course.rating && (
              <span className="backdrop-blur-sm bg-black/50 text-white text-sm font-semibold uppercase px-2 py-1 rounded-full flex items-center gap-1">
                <Star className="size-4 text-amber-400 fill-amber-400" />
                {course.rating}
              </span>
            )}
            {course.isPremium && (
              <span className="backdrop-blur-sm bg-white text-accent-500 text-xs font-bold uppercase px-2 py-1 rounded-full flex items-center gap-1">
                <Crown className="size-4" />
                Premium
              </span>
            )}
          </div>
        </div>
        {/* Content */}
        <div className="p-3 flex flex-col gap-3 flex-1">
          <div className="flex-1 flex flex-col gap-2">
            <p className="font-semibold text-gray-800 line-clamp-2">{course.title}</p>
            <p className="font-bold text-black">{formatPrice(course.price || 0)}</p>
          </div>
          {/* Stats */}
          <div className="flex items-center justify-between text-sm text-gray-400">
            <span className="flex items-center gap-1">
              <Eye className="size-4" />
              {course.viewCount || course.studentCount || 0}
            </span>
            <span className="flex items-center gap-1">
              <BookOpen className="size-4" />
              {course.lessonCount || 0} хичээл
            </span>
            <span className="flex items-center gap-1">
              <Clock className="size-4" />
              {course.totalHours ? `${course.totalHours} цаг` : course.duration || '180 мин'}
            </span>
          </div>
          <hr className="border-gray-200" />
          {/* Teacher + Date */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">{getTeacherName(course)}</span>
            <span className="text-gray-400">{course.createdAt ? formatDate(course.createdAt) : ''}</span>
          </div>
        </div>
      </div>
    </Link>
  )
}

/* ── Video Card ── */
function VideoCard({ video }: { video: any }) {
  const cover = resolveImg(video.thumbnail || video.thumbnailUrl || video.coverImage)
  return (
    <Link to={`/videos/${video._id}`} className="group">
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col">
        {/* Image with overlay info */}
        <div className="h-[170px] relative overflow-hidden shrink-0">
          {cover ? (
            <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-gray-700 to-gray-900" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
          {/* Top: stats pills */}
          <div className="absolute top-2 left-2 flex gap-2">
            <span className="backdrop-blur-sm bg-white text-gray-600 text-xs px-2 py-1 rounded-full flex items-center gap-1">
              <Eye className="size-3.5" />
              {video.viewCount || 0}
            </span>
            <span className="backdrop-blur-sm bg-white text-gray-600 text-xs px-2 py-1 rounded-full flex items-center gap-1">
              <Play className="size-3.5" />
              {video.duration || '180 мин'}
            </span>
          </div>
          {/* Bottom: teacher + date */}
          <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between text-sm text-white">
            <span>{getTeacherName(video)}</span>
            <span>{video.createdAt ? formatDate(video.createdAt) : ''}</span>
          </div>
        </div>
        {/* Title */}
        <div className="p-3 flex-1">
          <p className="font-semibold text-sm text-gray-800 line-clamp-2">{video.title}</p>
        </div>
      </div>
    </Link>
  )
}

/* ── Teacher Card ── */
function TeacherCard({ teacher }: { teacher: any }) {
  const name = formatName(teacher.userId?.fullName) || ''
  const cover = resolveImg(teacher.coverImage || teacher.userId?.avatar)

  return (
    <Link to={`/teachers/${teacher._id}`} className="group">
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col">
        {/* Cover */}
        <div className="h-[170px] relative overflow-hidden">
          {cover ? (
            <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-amber-100 to-amber-200" />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/25" />
        </div>
        {/* Info */}
        <div className="px-3 pb-3 pt-2 flex flex-col gap-3">
          <div>
            <p className="font-semibold text-gray-800">{name}</p>
            <p className="text-xs font-bold text-accent-500 uppercase">
              {teacher.title || teacher.specialty || 'Багш'}
            </p>
          </div>
          <hr className="border-gray-200" />
          {/* Stats */}
          <div className="flex items-center text-center text-sm">
            <div className="flex-1 flex flex-col gap-0.5">
              <span className="font-bold text-black">{teacher.followerCount || teacher.studentCount || 0}</span>
              <span className="text-gray-400">Дагагч</span>
            </div>
            <div className="flex-1 flex flex-col gap-0.5">
              <span className="font-bold text-black">{teacher.courseCount || 0}</span>
              <span className="text-gray-400">Хичээл</span>
            </div>
            <div className="flex-1 flex flex-col gap-0.5">
              <span className="font-bold text-black">{teacher.postCount || teacher.reviewCount || 0}</span>
              <span className="text-gray-400">Пост</span>
            </div>
          </div>
          <hr className="border-gray-200" />
          <button className="bg-gray-50 rounded-full py-2.5 text-sm font-semibold text-black hover:bg-gray-100 transition text-center">
            Хичээлүүд үзэх
          </button>
        </div>
      </div>
    </Link>
  )
}

/* ── Post Card (for horizontal scroll) ── */
function PostCard({ post }: { post: any }) {
  const image = resolveImg(post.imageUrl || post.image)
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden w-[282px] shrink-0 flex flex-col">
      <div className="h-[170px] relative overflow-hidden">
        {image ? (
          <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-amber-100 to-amber-200" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/25" />
      </div>
      <div className="p-3 flex flex-col gap-3">
        <span className="flex items-center gap-1 text-sm text-gray-400">
          <Clock className="size-4" />
          {post.createdAt ? formatDate(post.createdAt) : '2 минутын өмнө'}
        </span>
        <p className="font-semibold text-gray-800 truncate h-[38px]">
          {post.content || post.title || 'Сэтгэл засахүй'}
        </p>
        <hr className="border-gray-200" />
        <div className="flex gap-3 text-sm text-gray-400">
          <span className="flex items-center gap-1">
            <ThumbsUp className="size-4" />
            {post.likeCount || (Array.isArray(post.likes) ? post.likes.length : post.likes) || 0}
          </span>
          <span className="flex items-center gap-1 flex-1">
            <MessageCircle className="size-4" />
            {post.commentCount || (Array.isArray(post.comments) ? post.comments.length : post.comments) || 0}
          </span>
          <span className="flex items-center gap-1">
            <Share2 className="size-4" />
            {post.repostCount || (Array.isArray(post.reposts) ? post.reposts.length : post.reposts) || 0}
          </span>
        </div>
      </div>
    </div>
  )
}

/* ── Main Home ── */
export default function Home() {
  const { user } = useAuth()
  const [openFaq, setOpenFaq] = useState<number | null>(null)
  const [data, setData] = useState<any>(null)
  const [posts, setPosts] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [premiumStatus, setPremiumStatus] = useState<{ isPremium: boolean; subscription?: any } | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    Promise.all([
      api.home.get().then(setData).catch(console.error),
      api.posts.getFeed(1).then((res) => setPosts(res.data || [])).catch(() => {}),
    ]).finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (user) {
      api.premium.getStatus().then(setPremiumStatus).catch(() => {})
    } else {
      setPremiumStatus(null)
    }
  }, [user])

  const heroSlides = (data?.heroSlides || []).sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
  const [currentSlide, setCurrentSlide] = useState(0)

  // Auto-advance hero slider every 5 seconds
  useEffect(() => {
    if (heroSlides.length <= 1) return
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length)
    }, 5000)
    return () => clearInterval(timer)
  }, [heroSlides.length])

  const featuredTeachers = data?.featuredTeachers || []
  const courses = data?.featuredCourses || []
  const videos = data?.featuredVideos || []
  const teachers = data?.teachers || []
  const faqs = data?.faqs || []

  // Config sections from server
  const featuredSection = data?.featuredSection || {}
  const coursesSection = data?.coursesSection || {}
  const videosSection = data?.videosSection || {}
  const teachersSection = data?.teachersSection || {}
  const premiumCta = data?.premiumCta || {}
  const bottomCta = data?.bottomCta || {}

  const featuredTeacher = featuredTeachers[0]
  const featuredCourse = courses[0]
  const featuredVideo = videos[0]

  const scrollPosts = (dir: 'left' | 'right') => {
    scrollRef.current?.scrollBy({ left: dir === 'left' ? -306 : 306, behavior: 'smooth' })
  }

  if (loading) {
    return (
      <div>
        {/* Hero Skeleton */}
        <HeroSkeleton />

        {/* Featured Skeleton */}
        <section className="max-w-[1024px] mx-auto px-4 py-8">
          <div className="mb-6">
            <div className="h-7 w-48 bg-gray-200 animate-pulse rounded" />
            <div className="h-4 w-72 bg-gray-200 animate-pulse rounded mt-2" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-8">
            {[1, 2, 3].map((i) => <FeaturedCardSkeleton key={i} />)}
          </div>
        </section>

        {/* Courses Skeleton */}
        <section className="max-w-[1024px] mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="h-7 w-32 bg-gray-200 animate-pulse rounded" />
              <div className="h-4 w-64 bg-gray-200 animate-pulse rounded mt-2" />
            </div>
            <div className="h-10 w-36 bg-gray-200 animate-pulse rounded-full" />
          </div>
          <hr className="border-gray-200 mb-6" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => <CourseCardSkeleton key={i} />)}
          </div>
        </section>

        {/* Videos Skeleton */}
        <section className="max-w-[1024px] mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="h-7 w-40 bg-gray-200 animate-pulse rounded" />
              <div className="h-4 w-64 bg-gray-200 animate-pulse rounded mt-2" />
            </div>
            <div className="h-10 w-36 bg-gray-200 animate-pulse rounded-full" />
          </div>
          <hr className="border-gray-200 mb-6" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => <VideoCardSkeleton key={i} />)}
          </div>
        </section>

        {/* Teachers Skeleton */}
        <section className="max-w-[1024px] mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="h-7 w-28 bg-gray-200 animate-pulse rounded" />
              <div className="h-4 w-72 bg-gray-200 animate-pulse rounded mt-2" />
            </div>
            <div className="h-10 w-44 bg-gray-200 animate-pulse rounded-full" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => <TeacherCardSkeleton key={i} />)}
          </div>
        </section>

        {/* Posts Skeleton */}
        <section className="max-w-[1024px] mx-auto px-4 py-8">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-16">
            <div className="w-full lg:w-[320px] shrink-0 bg-gray-200 animate-pulse rounded-3xl h-[420px]" />
            <div className="flex-1 min-w-0 flex flex-col gap-6 justify-center">
              <div>
                <div className="h-7 w-48 bg-gray-200 animate-pulse rounded" />
                <div className="h-4 w-64 bg-gray-200 animate-pulse rounded mt-2" />
              </div>
              <div className="flex gap-6 overflow-hidden">
                {[1, 2, 3].map((i) => <PostCardSkeleton key={i} />)}
              </div>
            </div>
          </div>
        </section>
      </div>
    )
  }

  return (
    <div>
      {/* ═══ 0. Hero Slider ═══ */}
      {heroSlides.length > 0 && (
        <section className="relative w-full h-[400px] md:h-[500px] overflow-hidden">
          {heroSlides.map((slide: any, index: number) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-700 ${
                index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
            >
              {/* Background image */}
              <img
                src={resolveImg(slide.imageUrl)}
                alt={slide.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
              {/* Content */}
              <div className="relative z-10 h-full max-w-[1024px] mx-auto px-4 flex flex-col justify-center">
                <h1 className="text-3xl md:text-5xl font-bold text-white max-w-[600px] leading-tight">
                  {slide.title}
                </h1>
                {slide.subtitle && (
                  <p className="text-base md:text-lg text-white/80 mt-4 max-w-[500px]">
                    {slide.subtitle}
                  </p>
                )}
                {slide.buttonText && slide.buttonLink && (
                  <Link
                    to={slide.buttonLink}
                    className="mt-6 bg-accent-500 hover:bg-accent-600 text-white rounded-full px-8 py-3 text-sm font-semibold transition w-fit"
                  >
                    {slide.buttonText}
                  </Link>
                )}
              </div>
            </div>
          ))}

          {/* Prev / Next buttons */}
          {heroSlides.length > 1 && (
            <>
              <button
                onClick={() => setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length)}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white rounded-full p-2 transition cursor-pointer"
              >
                <ChevronLeft className="size-6" />
              </button>
              <button
                onClick={() => setCurrentSlide((prev) => (prev + 1) % heroSlides.length)}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white rounded-full p-2 transition cursor-pointer"
              >
                <ChevronRight className="size-6" />
              </button>
            </>
          )}

          {/* Dot indicators */}
          {heroSlides.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
              {heroSlides.map((_: any, index: number) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`rounded-full transition-all cursor-pointer ${
                    index === currentSlide
                      ? 'w-8 h-3 bg-white'
                      : 'w-3 h-3 bg-white/50 hover:bg-white/70'
                  }`}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {/* ═══ 1. Featured Content ═══ */}
      <section className="max-w-[1024px] mx-auto px-4 py-8">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-black">{featuredSection.title || 'Өнөөдрийн онцлох'}</h2>
          <p className="text-sm text-gray-500 mt-2">
            {featuredSection.subtitle || 'Бидний санал болгож буй шилдэг контентууд'}
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-8">
          {featuredTeacher && <FeaturedTeacherCard teacher={featuredTeacher} />}
          {featuredCourse && <FeaturedCourseCard course={featuredCourse} />}
          {featuredVideo ? (
            <FeaturedVideoCard video={featuredVideo} />
          ) : (
            courses[1] && <FeaturedCourseCard course={courses[1]} />
          )}
        </div>
      </section>

      {/* ═══ 2. Courses ═══ */}
      <section className="max-w-[1024px] mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-primary-500">{coursesSection.title || 'Сургалтууд'}</h2>
            <p className="text-sm text-gray-500 mt-2">
              {coursesSection.subtitle || 'Нийт 2,400 гаруй сургалтаас өөрт хэрэгцээтэйг хайж олоорой.'}
            </p>
          </div>
          <Link
            to="/courses"
            className="bg-primary-500 text-white rounded-full px-4 py-2.5 text-sm font-semibold shadow-xs flex items-center gap-2 hover:bg-primary-600 transition whitespace-nowrap"
          >
            Бүх хичээл үзэх
            <ArrowRight className="size-5" />
          </Link>
        </div>
        <hr className="border-gray-200 mb-6" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {courses.slice(0, 4).map((course: any) => (
            <CourseCard key={course._id} course={course} />
          ))}
        </div>
      </section>

      {/* ═══ 3. Videos ═══ */}
      <section className="max-w-[1024px] mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-accent-500">{videosSection.title || 'Видео бичлэгүүд'}</h2>
            <p className="text-sm text-gray-500 mt-2">
              {videosSection.subtitle || 'Нийт 2,400 гаруй видео бичлэгүүдээс өөрт хэрэгцээтэйг хайж олоорой.'}
            </p>
          </div>
          <Link
            to="/videos"
            className="bg-accent-500 text-white rounded-full px-4 py-2.5 text-sm font-semibold shadow-xs flex items-center gap-2 hover:bg-accent-600 transition whitespace-nowrap"
          >
            Бүх видео үзэх
            <ArrowRight className="size-5" />
          </Link>
        </div>
        <hr className="border-gray-200 mb-6" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {videos.slice(0, 4).map((video: any) => (
            <VideoCard key={video._id} video={video} />
          ))}
        </div>
      </section>

      {/* ═══ 4. Teachers ═══ */}
      <section className="max-w-[1024px] mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl lg:text-4xl font-bold text-black">{teachersSection.title || 'Багш нар'}</h2>
            <p className="text-sm text-gray-500 mt-2">
              {teachersSection.subtitle || 'Таны суралцах замд чиглүүлэгч, туршлагатай багш нараас суралцаарай.'}
            </p>
          </div>
          <Link
            to="/teachers"
            className="bg-white border border-gray-200 text-gray-800 rounded-full px-4 py-2.5 text-sm font-semibold shadow-xs flex items-center gap-2 hover:bg-gray-50 transition whitespace-nowrap"
          >
            Бүх багш нарыг харах
            <ArrowRight className="size-5" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {teachers.slice(0, 4).map((teacher: any) => (
            <TeacherCard key={teacher._id} teacher={teacher} />
          ))}
        </div>
      </section>

      {/* ═══ 5. Premium CTA ═══ */}
      <section className="max-w-[1024px] mx-auto px-4 py-8">
        {premiumStatus?.isPremium ? (
          <div
            className="border border-green-300 rounded-2xl p-8 flex items-center justify-between overflow-hidden"
            style={{
              background:
                'linear-gradient(90deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.98) 100%), linear-gradient(90deg, #22c55e 0%, #22c55e 100%)',
            }}
          >
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <Crown className="size-3.5 text-green-600" />
                <span className="text-xs font-bold text-green-600 uppercase">
                  Premium идэвхтэй
                </span>
              </div>
              <div className="flex flex-col gap-2">
                <h3 className="text-2xl font-bold text-black">
                  Та Premium гишүүн байна
                </h3>
                <p className="text-sm text-gray-600 max-w-[640px]">
                  Манай платформын бүх сургалт, видео сан болон тусгай контентуудыг хязгааргүй үзэх боломжтой.
                  {premiumStatus.subscription?.endDate && (
                    <> Эрхийн хугацаа: <strong>{formatDate(premiumStatus.subscription.endDate)}</strong> хүртэл.</>
                  )}
                </p>
              </div>
            </div>
            <Link
              to="/courses"
              className="bg-green-600 text-white rounded-full px-9 py-4 text-base font-semibold shadow-sm hover:bg-green-700 transition shrink-0"
            >
              Сургалт үзэх
            </Link>
          </div>
        ) : (
          <div
            className="border border-accent-300 rounded-2xl p-8 flex items-center justify-between overflow-hidden"
            style={{
              background:
                'linear-gradient(90deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.98) 100%), linear-gradient(90deg, #ff4d00 0%, #ff4d00 100%)',
            }}
          >
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <Sparkles className="size-3.5 text-accent-500" />
                <span className="text-xs font-bold text-accent-500 uppercase">
                  Premium Membership
                </span>
              </div>
              <div className="flex flex-col gap-2">
                <h3 className="text-2xl font-bold text-black">
                  {premiumCta.title || 'Бүх сургалтыг хязгааргүй үзэх'}
                </h3>
                <p className="text-sm text-gray-600 max-w-[640px]">
                  {premiumCta.subtitle || 'Premium эрх авснаар манай платформын бүх сургалт, видео сан болон тусгай контентуудыг хязгааргүй үзэх боломжтой болно.'}
                </p>
              </div>
            </div>
            <Link
              to="/premium"
              className="bg-accent-500 text-white rounded-full px-9 py-4 text-base font-semibold shadow-sm hover:bg-accent-600 transition shrink-0"
            >
              {premiumCta.buttonText || 'Одоо эхэл'}
            </Link>
          </div>
        )}
      </section>

      {/* ═══ 6. Featured User + Recent Posts ═══ */}
      <section className="max-w-[1024px] mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-16">
          {/* Featured User Card */}
          {featuredTeacher && (
          <div className="w-full lg:w-[320px] shrink-0 bg-primary-500 border border-gray-200 rounded-3xl p-6 flex flex-col justify-between overflow-hidden relative">
            {/* Decorative star */}
            <div className="absolute -bottom-16 -right-16 opacity-50">
              <Sparkles className="size-48 text-white/20 rotate-[166deg]" />
            </div>
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/25 pointer-events-none" />

            <div className="relative z-10 flex flex-col gap-4 flex-1">
              <span className="backdrop-blur-sm bg-black/50 text-white text-base font-semibold px-1.5 py-1 rounded w-fit">
                Онцлох хэрэглэгч
              </span>

              <div className="flex-1 flex flex-col gap-4 mt-4">
                <div className="flex items-center gap-3">
                  <div className="border-2 border-white rounded-full overflow-hidden size-[46px] shrink-0">
                    <Avatar
                      src={resolveImg(featuredTeacher.userId?.avatar)}
                      alt=""
                      size="md"
                      className="size-[46px]"
                    />
                  </div>
                  <div>
                    <p className="font-semibold text-white">
                      {formatName(featuredTeacher.userId?.fullName) || ''}
                    </p>
                    <p className="text-xs font-bold text-white/70 uppercase">
                      {featuredTeacher.title || featuredTeacher.specialty || 'Багш'}
                    </p>
                  </div>
                </div>

                <p className="text-sm text-white/90 line-clamp-3">
                  {featuredTeacher.userId?.bio || featuredTeacher.description || ''}
                </p>

                <div className="flex gap-3">
                  <div className="flex-1 bg-white/10 rounded-xl py-3 flex flex-col items-center gap-0.5">
                    <span className="text-sm font-bold text-white">{featuredTeacher.followerCount ?? 0}</span>
                    <span className="text-sm text-gray-200">Дагагч</span>
                  </div>
                  <div className="flex-1 bg-white/10 rounded-xl py-3 flex flex-col items-center gap-0.5">
                    <span className="text-sm font-bold text-white">{featuredTeacher.postCount ?? 0}</span>
                    <span className="text-sm text-gray-200">Пост</span>
                  </div>
                  <div className="flex-1 bg-white/10 rounded-xl py-3 flex flex-col items-center gap-0.5">
                    <span className="text-sm font-bold text-white">{featuredTeacher.courseCount ?? 0}</span>
                    <span className="text-sm text-gray-200">Хичээл</span>
                  </div>
                </div>
              </div>
            </div>

            <Link
              to={`/teachers/${featuredTeacher._id}`}
              className="relative z-10 bg-gray-50 rounded-full py-2.5 text-sm font-semibold text-black hover:bg-white transition text-center w-full mt-4 block"
            >
              Профайл үзэх
            </Link>
          </div>
          )}

          {/* Recent Posts */}
          <div className="flex-1 min-w-0 flex flex-col gap-6 justify-center">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-black">Сүүлийн нийтлэлүүд</h2>
                <p className="text-sm text-gray-500 mt-2">
                  Хэрэглэгчийн нийтэлсэн онцлох постууд
                </p>
              </div>
              <Link
                to="/feed"
                className="bg-white border border-gray-200 text-gray-800 rounded-full px-4 py-2.5 text-sm font-semibold shadow-xs flex items-center gap-2 hover:bg-gray-50 transition whitespace-nowrap"
              >
                Бүгдийг үзэх
                <ArrowRight className="size-5" />
              </Link>
            </div>

            {/* Horizontal scroll container */}
            <div className="relative">
              <div
                ref={scrollRef}
                className="flex gap-6 overflow-x-auto scroll-smooth no-scrollbar"
              >
                {posts.slice(0, 6).map((post: any, i: number) => (
                  <PostCard key={post._id || i} post={post} />
                ))}
                {posts.length === 0 &&
                  [1, 2, 3].map((i) => (
                    <PostCard key={i} post={{ title: 'Сэтгэл засахүй', likes: 120, comments: 120, reposts: 120 }} />
                  ))}
              </div>
              {/* Carousel arrows */}
              <button
                onClick={() => scrollPosts('left')}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white border border-gray-200 rounded-full p-2.5 shadow-xs hover:bg-gray-50 transition cursor-pointer"
              >
                <ChevronLeft className="size-5" />
              </button>
              <button
                onClick={() => scrollPosts('right')}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white border border-gray-200 rounded-full p-2.5 shadow-xs hover:bg-gray-50 transition cursor-pointer"
              >
                <ChevronRight className="size-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ 7. FAQ ═══ */}
      <section className="max-w-[1024px] mx-auto px-4 py-16">
        <div className="text-center mb-16">
          <h2 className="text-2xl lg:text-4xl font-bold text-black">Түгээмэл асуултууд</h2>
          <p className="text-sm text-gray-500 mt-4">
            Танд хэрэгцээтэй хариултууд энд бий.
          </p>
        </div>
        <div className="flex flex-col gap-4">
          {faqs.map((faq: any, i: number) => (
            <FAQItem
              key={i}
              item={faq}
              isOpen={openFaq === i}
              onClick={() => setOpenFaq(openFaq === i ? null : i)}
            />
          ))}
          {faqs.length === 0 &&
            [
              'Сургалтанд хэрхэн бүртгүүлэх вэ?',
              'Сургалтанд хэрхэн бүртгүүлэх вэ?',
              'Сургалтанд хэрхэн бүртгүүлэх вэ?',
              'Сургалтанд хэрхэн бүртгүүлэх вэ?',
            ].map((q, i) => (
              <FAQItem
                key={i}
                item={{ question: q, answer: 'Та манай платформд бүртгүүлж, хүссэн сургалтаа сонгоод бүртгэлийн хэсгээс бүртгүүлнэ үү.' }}
                isOpen={openFaq === i}
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              />
            ))}
        </div>
      </section>

      {/* ═══ 8. Bottom CTA - App Download ═══ */}
      <section className="max-w-[1024px] mx-auto px-4 py-8 pb-16">
        <div className="bg-primary-500 rounded-[22px] px-8 md:px-16 py-8 md:py-10 flex flex-col lg:flex-row items-center gap-10 overflow-hidden relative">
          {/* Left: Text + QR */}
          <div className="flex-1 flex flex-col gap-8">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-1.5 bg-white rounded-full px-2.5 py-1 w-fit">
                <Sparkles className="size-3 text-gray-800" />
                <span className="text-[10px] font-medium text-gray-800">
                  {bottomCta.buttonText || 'Arya апп: Та яаг одоо эхэл.'}
                </span>
              </div>
              <h2 className="text-3xl lg:text-[44px] font-bold text-white leading-tight">
                {bottomCta.title || 'Тавтай морилно уу.'}
              </h2>
              <p className="text-[11px] text-white max-w-[393px] leading-normal">
                {bottomCta.subtitle || 'Та ARYA платформоор дамжуулан манай төвөөс явуулдаг анхан дунд гүнзгий шатны зайны сургалтуудад хамрагдаарай'}
              </p>
            </div>

            {/* QR codes container */}
            <div className="border border-dashed border-white rounded-[18px] p-4 flex gap-4 w-fit">
              {/* Android card */}
              <div className="bg-white rounded-xl px-3 pb-4 flex flex-col items-center gap-3 w-full">
                <div className="flex items-center gap-1.5 pt-4 pb-2">
                  <svg className="size-4" viewBox="0 0 24 24" fill="none"><path d="M17.523 15.341a.6.6 0 0 0 .6-.6V8.696a2.104 2.104 0 0 0-2.1-2.1h-.54l.96-1.74a.6.6 0 0 0-1.052-.58l-1.06 1.92h-.12l-.54-.54.24-1.38a.6.6 0 1 0-1.182-.204l-.18 1.044h-.18l-.18-1.044a.6.6 0 1 0-1.182.204l.24 1.38-.54.54h-.12L9.67 4.276a.6.6 0 0 0-1.052.58l.96 1.74h-.54a2.104 2.104 0 0 0-2.1 2.1v6.045a.6.6 0 0 0 .6.6h.6v2.4a1.2 1.2 0 0 0 2.4 0v-2.4h2.4v2.4a1.2 1.2 0 0 0 2.4 0v-2.4h.6zm-11.04-5.7a1.2 1.2 0 0 0-1.2 1.2v2.4a1.2 1.2 0 0 0 2.4 0v-2.4a1.2 1.2 0 0 0-1.2-1.2zm11.04 0a1.2 1.2 0 0 0-1.2 1.2v2.4a1.2 1.2 0 0 0 2.4 0v-2.4a1.2 1.2 0 0 0-1.2-1.2z" fill="currentColor"/></svg>
                  <span className="text-[10px] font-semibold text-black uppercase">ANDROID</span>
                </div>
                <img src="/app-download/qr-android.svg" alt="Android QR" className="size-[78px]" />
                <div className="flex items-center gap-1.5 w-full">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-[8px] font-semibold text-gray-800 uppercase">ЭСВЭЛ</span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
                <a href="https://play.google.com/store" target="_blank" rel="noopener noreferrer">
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                    alt="Google Play"
                    className="h-7"
                  />
                </a>
              </div>

              {/* iOS card */}
              <div className="bg-white rounded-xl px-3 pb-4 flex flex-col items-center gap-3 w-full">
                <div className="flex items-center gap-1.5 pt-4 pb-2">
                  <svg className="size-4" viewBox="0 0 24 24" fill="none"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" fill="currentColor"/></svg>
                  <span className="text-[10px] font-semibold text-black uppercase">IOS</span>
                </div>
                <img src="/app-download/qr-ios.svg" alt="iOS QR" className="size-[78px]" />
                <div className="flex items-center gap-1.5 w-full">
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-[8px] font-semibold text-gray-800 uppercase">ЭСВЭЛ</span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
                <a href="https://apps.apple.com" target="_blank" rel="noopener noreferrer">
                  <img
                    src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                    alt="App Store"
                    className="h-7"
                  />
                </a>
              </div>
            </div>
          </div>

          {/* Right: Phone mockups */}
          <div className="hidden lg:block w-[412px] h-[387px] shrink-0 relative">
            {/* Background shape */}
            <img
              src="/app-download/union-bg.svg"
              alt=""
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[340px] h-[372px] opacity-30"
            />
            {/* Left phone (rotated) */}
            <div
              className="absolute left-0 top-0 w-[207px] h-[312px] z-10"
              style={{ transform: 'rotate(-28.7deg) skewX(2.5deg)' }}
            >
              <img
                src="/app-download/phone-left.png"
                alt="ARYA App"
                className="absolute w-[93.5%] h-[94.4%] top-[2.8%] left-[3.3%] object-cover rounded-[16px] z-100"
              />
              <img
                src="/app-download/phone-frame-left.png"
                alt=""
                className="absolute inset-0 w-full h-full z-10 pointer-events-none"
              />
            </div>
            {/* Right phone (slight tilt) */}
            <div
              className="absolute right-[10px] bottom-0 w-[232px] h-[286px] z-20"
              style={{ transform: 'rotate(-5.1deg)' }}
            >
              <img
                src="/app-download/phone-right.png"
                alt="ARYA App"
                className="absolute w-[92.6%] h-[88%] top-[6%] left-[3.7%] object-cover rounded-[20px] z-100"
              />
              <img
                src="/app-download/phone-frame-right.png"
                alt=""
                className="absolute inset-0 w-full h-full z-10 pointer-events-none"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
